/**
 * Utilidades de versión para el aviso de actualización del cliente de firma (#59, F1B-7).
 *
 * Compara la versión instalada (reportada por el daemon local en GET /status) contra la
 * versión vigente del manifest del backend. Tolera sufijos de pre-release (p.ej.
 * "0.1.0-SNAPSHOT") comparando solo el core numérico "MAJOR.MINOR.PATCH".
 */

/** Normaliza "1.2.3-SNAPSHOT+build" → [1, 2, 3]. Partes no numéricas → 0. */
const coreParts = (version) => {
  const core = String(version || '').trim().split(/[-+]/)[0];
  const parts = core.split('.').map((p) => {
    const n = parseInt(p, 10);
    return Number.isNaN(n) ? 0 : n;
  });
  while (parts.length < 3) parts.push(0);
  return parts.slice(0, 3);
};

/**
 * Compara dos versiones por su core numérico.
 * @returns {number} -1 si a < b, 0 si iguales, 1 si a > b.
 */
export const compareVersions = (a, b) => {
  const pa = coreParts(a);
  const pb = coreParts(b);
  for (let i = 0; i < 3; i += 1) {
    if (pa[i] < pb[i]) return -1;
    if (pa[i] > pb[i]) return 1;
  }
  return 0;
};

/**
 * ¿La versión instalada está por detrás de la vigente?
 * Requiere ambas versiones válidas; si falta alguna devuelve false (no avisar a ciegas).
 */
export const isOutdated = (installed, latest) => {
  if (!installed || !latest) return false;
  return compareVersions(installed, latest) < 0;
};

/**
 * ¿La versión instalada quedó por debajo del mínimo que el servidor exige para firmar? (#108)
 *
 * <p>Distinto de {@link isOutdated}, que sólo motiva un aviso: acá el cliente ya no puede cumplir el
 * contrato. Un cliente que ignora el nivel PAdES firma todo sin sello, y esas firmas se rechazan al
 * publicarlas — con el PIN ya gastado y una operación del token quemada por documento. Es mejor no
 * empezar.
 *
 * <p>Si el manifest no declara mínimo, o el daemon no reporta versión, devuelve {@code false}: no se
 * bloquea a nadie por datos que no se tienen.
 */
export const isBelowMinimum = (installed, minimum) => {
  if (!installed || !minimum) return false;
  return compareVersions(installed, minimum) < 0;
};

/**
 * Detecta el SO del navegador → clave del manifest ('windows' | 'macos' | 'linux' | null).
 * Usa navigator.userAgentData.platform si está, con fallback a userAgent/platform.
 */
export const detectOs = (nav = typeof navigator !== 'undefined' ? navigator : {}) => {
  const hint = (nav.userAgentData?.platform || nav.platform || nav.userAgent || '').toLowerCase();
  if (hint.includes('win')) return 'windows';
  if (hint.includes('mac') || hint.includes('darwin')) return 'macos';
  if (hint.includes('linux') || hint.includes('x11')) return 'linux';
  return null;
};
